import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
import './EventPreview.css';

const EventPreview = ({
  event = null,
  isOpen = false,
  position = { x: 0, y: 0 },
  onClose = () => {},
  onExpand = () => {},
  onEdit = () => {},
  onDelete = () => {},
  onSave = null,
  onCreate = null,
  employees = [],
  // New props for event creation
  selectedDate = null,
  selectedHour = null,
  isCreating = false
}) => {
  // Individual field editing states
  const [editingTitle, setEditingTitle] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [editingTime, setEditingTime] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [editingParticipants, setEditingParticipants] = useState(false);

  // Editable field values
  const [editedTitle, setEditedTitle] = useState('');
  const [editedLocation, setEditedLocation] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedStartTime, setEditedStartTime] = useState('');
  const [editedEndTime, setEditedEndTime] = useState('');

  // Participants state
  const [participantSearch, setParticipantSearch] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [showParticipantsDropdown, setShowParticipantsDropdown] = useState(false);

  // New state for description dropdown
  const [descriptionMode, setDescriptionMode] = useState('notes');

  // New state for expanded description options
  const [showDescriptionOptions, setShowDescriptionOptions] = useState(false);

  // File upload state
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);
  const participantInputRef = useRef(null);

  // Zoom conferencing state
  const [zoomInfo, setZoomInfo] = useState(null);
  const [isCreatingZoom, setIsCreatingZoom] = useState(false);

  // File handling functions
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }, []);

  const handleFileUpload = useCallback((files) => {
    const newFiles = Array.from(files).map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      originalName: file.name
    }));
    setUploadedFiles(prev => [...prev, ...newFiles]);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files);
    }
  }, [handleFileUpload]);

  const handleFileInputChange = useCallback((e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileUpload(files);
    }
  }, [handleFileUpload]);

  const removeFile = useCallback((index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Add createZoomMeeting function
  const createZoomMeeting = async () => {
    if (!editedTitle.trim()) {
      alert('Please enter a title for the meeting.');
      return;
    }

    setIsCreatingZoom(true);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token not found.');
      }

      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://optivo-backend-production.up.railway.app';
      
      // Create proper Date objects from the event data
      let meetingStartTime, meetingEndTime;
      
      if (selectedDate && editedStartTime && editedEndTime) {
        // selectedDate is a Date object, editedStartTime and editedEndTime are strings like "14:00:00"
        const baseDate = new Date(selectedDate);
        
        // Handle time strings like "14:00:00"
        const [startHour, startMinute] = editedStartTime.split(':').map(Number);
        const [endHour, endMinute] = editedEndTime.split(':').map(Number);
        
        meetingStartTime = new Date(baseDate);
        meetingStartTime.setHours(startHour, startMinute, 0, 0);
        
        meetingEndTime = new Date(baseDate);
        meetingEndTime.setHours(endHour, endMinute, 0, 0);
        
        console.log('📅 Parsed meeting times:', {
          baseDate: baseDate.toISOString(),
          startTime: editedStartTime,
          endTime: editedEndTime,
          meetingStartTime: meetingStartTime.toISOString(),
          meetingEndTime: meetingEndTime.toISOString()
        });
      } else if (event) {
        // Use existing event data
        const eventDate = new Date(event.shift_date);
        if (event.start_time && event.end_time) {
          const [startHour, startMinute] = event.start_time.split(':').map(Number);
          const [endHour, endMinute] = event.end_time.split(':').map(Number);
          
          meetingStartTime = new Date(eventDate);
          meetingStartTime.setHours(startHour, startMinute, 0, 0);
          
          meetingEndTime = new Date(eventDate);
          meetingEndTime.setHours(endHour, endMinute, 0, 0);
        } else {
          // Fallback for all-day events
          meetingStartTime = new Date();
          meetingEndTime = new Date(meetingStartTime.getTime() + 30 * 60 * 1000);
        }
      } else {
        // Fallback to current time + 30 minutes
        meetingStartTime = new Date();
        meetingEndTime = new Date(meetingStartTime.getTime() + 30 * 60 * 1000);
        
        console.log('📅 Using fallback times:', {
          meetingStartTime: meetingStartTime.toISOString(),
          meetingEndTime: meetingEndTime.toISOString()
        });
      }

      const duration = Math.round((meetingEndTime - meetingStartTime) / (1000 * 60)); // Duration in minutes

      console.log('🎯 Creating Zoom meeting with:', {
        title: editedTitle,
        startTime: meetingStartTime.toISOString(),
        duration: duration + ' minutes'
      });

      const response = await fetch(`${API_BASE_URL}/api/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          title: editedTitle,
          startTime: meetingStartTime.toISOString(),
          duration: duration
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Zoom API Error:', {
          status: response.status,
          statusText: response.statusText,
          errorText: errorText
        });
        throw new Error(`Failed to create Zoom meeting: ${response.status} ${response.statusText}`);
      }

      const meetingData = await response.json();
      console.log('✅ Zoom meeting created successfully:', meetingData);

      // Store meeting info in state
      setZoomInfo({
        id: meetingData.id,
        passcode: meetingData.password,
        joinUrl: meetingData.join_url
      });
      
      console.log('🎉 Zoom meeting info stored in state');

      // Auto-save the zoom info to the event if it's an existing event
      if (event && onSave) {
        await onSave({ 
          ...event, 
          zoom_info: {
            id: meetingData.id,
            passcode: meetingData.password,
            joinUrl: meetingData.join_url
          }
        });
      }

    } catch (error) {
      console.error('❌ Error creating Zoom meeting:', error);
      alert(`Failed to create Zoom meeting: ${error.message}`);
    } finally {
      setIsCreatingZoom(false);
    }
  };

  // Initialize editable fields when event changes or when creating new event
  useEffect(() => {
    if (event) {
      // Editing existing event
      setEditedTitle(event.title || '');
      setEditedLocation(event.location || '');
      setEditedDescription(event.description || '');
      setEditedStartTime(event.start_time || '');
      setEditedEndTime(event.end_time || '');
      
      // Load existing participants
      let participantEmails = [];
      if (event.participants && Array.isArray(event.participants)) {
        participantEmails = event.participants;
      }
      
      if (participantEmails.length > 0 && employees.length > 0) {
        const participantObjects = participantEmails.map(email => {
          const employee = employees.find(emp => emp.email === email);
          return employee || { id: 0, email, first_name: '', last_name: '', name: '', company_id: 0 };
        });
        setSelectedParticipants(participantObjects);
      } else {
        setSelectedParticipants([]);
      }
      
      // Load existing files if any
      if (event.files && Array.isArray(event.files)) {
        setUploadedFiles(event.files);
      } else {
        setUploadedFiles([]);
      }

      // Load existing Zoom info if any
      if (event.zoom_info) {
        console.log('🎯 Loading existing Zoom info:', event.zoom_info);
        setZoomInfo(event.zoom_info);
      } else {
        console.log('🎯 No Zoom info found in existing event');
        setZoomInfo(null);
      }
    } else if (isCreating && selectedDate) {
      // Creating new event
      setEditedTitle('');
      setEditedLocation('');
      setEditedDescription('');
      
      // Set default times based on selectedHour
      if (selectedHour !== undefined) {
        if (selectedHour === -1) {
          // All-day event - explicitly set empty times to indicate all-day
          setEditedStartTime('');
          setEditedEndTime('');
        } else if (selectedHour >= 0) {
          // Timed event
          const hour = Math.floor(selectedHour);
          const minute = Math.round((selectedHour - hour) * 60);
          const endHour = hour + 1;
          
          const startTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
          const endTime = `${String(endHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
          
          setEditedStartTime(startTime);
          setEditedEndTime(endTime);
        }
      } else {
        // Default to empty times (all-day)
        setEditedStartTime('');
        setEditedEndTime('');
      }
      
      setSelectedParticipants([]);
      // Auto-focus title for new events
      setEditingTitle(true);
      setUploadedFiles([]);
    }
  }, [event, employees, isCreating, selectedDate, selectedHour]);

  // Filter employees for dropdown
  const filteredEmployees = useMemo(() => {
    if (participantSearch.trim() === '') {
      return [];
    }

    return employees.filter(emp => {
      const searchTerm = participantSearch.toLowerCase();
      const fullName = `${emp.first_name || ''} ${emp.last_name || ''}`.toLowerCase().trim();
      const email = emp.email.toLowerCase();
      
      const isAlreadySelected = selectedParticipants.some(p => p.email === emp.email);
      
      return !isAlreadySelected && (
        email.includes(searchTerm) ||
        fullName.includes(searchTerm) ||
        (emp.first_name && emp.first_name.toLowerCase().includes(searchTerm)) ||
        (emp.last_name && emp.last_name.toLowerCase().includes(searchTerm))
      );
    });
  }, [participantSearch, employees, selectedParticipants]);

  // Participant management functions
  const handleParticipantSelect = (employee) => {
    const newParticipants = [...selectedParticipants, employee];
    setSelectedParticipants(newParticipants);
    setParticipantSearch('');
    setShowParticipantsDropdown(false);
    
    // Auto-save participants immediately for existing events
    if (onSave && event && !isCreating) {
      onSave({ 
        ...event, 
        participants: newParticipants.map(p => p.email) 
      }).catch(error => {
        console.error('Failed to save participants:', error);
        // Revert on error
        setSelectedParticipants(selectedParticipants);
      });
    }
  };

  const handleParticipantRemove = (emailToRemove) => {
    const newParticipants = selectedParticipants.filter(p => p.email !== emailToRemove);
    setSelectedParticipants(newParticipants);
    
    // Auto-save participants immediately for existing events
    if (onSave && event && !isCreating) {
      onSave({ 
        ...event, 
        participants: newParticipants.map(p => p.email) 
      }).catch(error => {
        console.error('Failed to save participants:', error);
        // Revert on error
        setSelectedParticipants(selectedParticipants);
      });
    }
  };

  // Update dropdown visibility
  useEffect(() => {
    setShowParticipantsDropdown(filteredEmployees.length > 0 && participantSearch.trim() !== '');
  }, [filteredEmployees, participantSearch]);

  const formatTime = (timeString) => {
    if (!timeString) return 'All day';
    return moment(timeString, 'HH:mm:ss').format('h:mm A');
  };

  const formatDate = (dateString) => {
    return moment(dateString).format('MMM D, YYYY');
  };

  const formatTimeRange = () => {
    if (isCreating && selectedDate) {
      try {
        // Ensure selectedDate is a valid Date object
        const validDate = selectedDate instanceof Date && !isNaN(selectedDate) 
          ? selectedDate 
          : new Date(selectedDate);
        
        if (isNaN(validDate)) {
          return 'Invalid date';
        }
        
        const dateString = validDate.toISOString().split('T')[0];
        
        if (!editedStartTime || !editedEndTime) {
          return `${formatDate(dateString)}`;
        }
        return `${formatDate(dateString)} ${formatTime(editedStartTime)} to ${formatTime(editedEndTime)}`;
      } catch (error) {
        console.error('Error formatting date in formatTimeRange:', error);
        return 'Invalid date';
      }
    }
    
    if (!event) return '';
    if (!event.start_time || !event.end_time) {
      return `${formatDate(event.shift_date)}`;
    }
    return `${formatDate(event.shift_date)} ${formatTime(event.start_time)} to ${formatTime(event.end_time)}`;
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      setShowDescriptionOptions(false);
      
      // Auto-save any changes before closing (for existing events)
      if (!isCreating && event && onSave) {
        // Check if any field has been modified
        const hasChanges = 
          editedTitle !== (event.title || '') ||
          editedLocation !== (event.location || '') ||
          editedDescription !== (event.description || '') ||
          editedStartTime !== (event.start_time || '') ||
          editedEndTime !== (event.end_time || '') ||
          JSON.stringify(selectedParticipants.map(p => p.email).sort()) !== 
          JSON.stringify((event.participants || []).sort());
        
        if (hasChanges) {
          // Save all changes
          const updatedData = {
            title: editedTitle,
            location: editedLocation,
            description: editedDescription,
            start_time: editedStartTime,
            end_time: editedEndTime,
            participants: selectedParticipants.map(p => p.email)
          };
          
          onSave({ ...event, ...updatedData }).catch(error => {
            console.error('Failed to auto-save changes:', error);
          });
        }
      }
      
      onClose();
    }
  };

  const handleCreateEvent = async () => {
    if (!isCreating || !onCreate || !selectedDate) return;
    
    if (!editedTitle.trim()) {
      alert('Please enter a title for the event');
      return;
    }

    try {
      // Ensure selectedDate is a valid Date object
      const validDate = selectedDate instanceof Date && !isNaN(selectedDate) 
        ? selectedDate 
        : new Date(selectedDate);
      
      if (isNaN(validDate)) {
        console.error('Invalid selectedDate:', selectedDate);
        alert('Invalid date selected. Please try again.');
        return;
      }
      
      const year = validDate.getFullYear();
      const month = String(validDate.getMonth() + 1).padStart(2, '0');
      const day = String(validDate.getDate()).padStart(2, '0');
      const formattedDate = `${year}-${month}-${day}`;

      // Format time based on selectedHour
      let startTime = editedStartTime || null;
      let endTime = editedEndTime || null;
      let isAllDay = false;
      
      // Determine if this should be an all-day event
      if (selectedHour === -1) {
        // Explicitly clicked on all-day section
        isAllDay = true;
      } else if (!startTime || !endTime || startTime.trim() === '' || endTime.trim() === '') {
        // No times specified, treat as all-day
        isAllDay = true;
      } else {
        // Has valid start and end times
        isAllDay = false;
      }
      
      // If no times are set but we have a selectedHour >= 0, create default times
      if (!isAllDay && (!startTime || !endTime) && selectedHour !== undefined && selectedHour >= 0) {
        const hour = Math.floor(selectedHour);
        const minute = Math.round((selectedHour - hour) * 60);
        const endHour = hour + 1;
        
        startTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
        endTime = `${String(endHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:00`;
        isAllDay = false;
      }

      // Create base event data without time fields
      const baseEventData = {
        title: editedTitle.trim(),
        description: editedDescription || '',
        shift_date: formattedDate,
        end_date: formattedDate,
        is_all_day: isAllDay,
        location: editedLocation || '',
        calendar_type: 'shift',
        color: '#4263eb',
        participants: selectedParticipants.map(p => p.email),
        recurrence_rule: null,
        timezone: 'America/New_York',
        files: uploadedFiles.length > 0 ? uploadedFiles : undefined,
        zoom_info: zoomInfo || undefined,
        resource: event?.resource || event?.employee?.id,
        start: event?.start,
        end: event?.end
      };

      // Add time fields only for non-all-day events
      const eventData = isAllDay 
        ? {
            ...baseEventData,
            start_time: '00:00:00',
            end_time: '23:59:59'
          }
        : {
            ...baseEventData,
            start_time: startTime,
            end_time: endTime
          };

      console.log('🎭 EventPreview creating eventData:', eventData);

      await onCreate(eventData);
      onClose();
    } catch (error) {
      console.error('Failed to create event:', error);
      alert('Failed to create event. Please try again.');
    }
  };

  const handleKeyDown = (e, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (field === 'title' && isCreating) {
        handleCreateEvent();
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingTitle(false);
      setEditingLocation(false);
      setEditingTime(false);
      setEditingDescription(false);
      setEditingParticipants(false);
    }
  };

  // Add missing description options handler
  const handleDescriptionOptionSelect = (option) => {
    setDescriptionMode(option);
    
    if (option === 'attachments') {
      // Open file picker and close options
      fileInputRef.current?.click();
      setShowDescriptionOptions(false);
    } else {
      // For notes and URL, start editing and close options
      setEditingDescription(true);
      setShowDescriptionOptions(false);
      
      if (option === 'url' && !editedDescription.startsWith('http')) {
        setEditedDescription('https://');
      }
    }
  };

  const getDescriptionPlaceholder = () => {
    switch (descriptionMode) {
      case 'notes':
        return 'Add notes or description...';
      case 'url':
        return 'Add URL link...';
      case 'attachments':
        return 'Add attachment description...';
      default:
        return 'Add description...';
    }
  };

  const handleBlur = (field) => {
    // Auto-save on blur for existing events
    if (!isCreating && event && onSave) {
      const updatedData = {};
      
      if (field === 'description') {
        updatedData.description = editedDescription;
      }
      
      if (Object.keys(updatedData).length > 0) {
        onSave({ ...event, ...updatedData }).catch(error => {
          console.error('Failed to save field:', error);
        });
      }
    }
  };

  if (!isOpen || (!event && !isCreating)) return null;

  // Calculate positioning with expansion in mind
  const previewWidth = showDescriptionOptions ? 320 : 280;
  const previewHeight = 300; // Approximate height
  
  let leftPosition, topPosition, showArrow = false;
  
  // Try to position to the right of the clicked event first
  const rightPosition = position.x + 60; // Increased offset from click point
  const hasSpaceOnRight = rightPosition + previewWidth <= window.innerWidth - 20;
  
  if (hasSpaceOnRight) {
    // Position to the right with arrow
    leftPosition = rightPosition;
    showArrow = true;
    // Center vertically relative to the click point
    topPosition = Math.max(20, Math.min(position.y - previewHeight / 2, window.innerHeight - previewHeight - 20));
  } else {
    // Fall back to centered positioning without arrow
    leftPosition = position.x - (previewWidth / 2);
    topPosition = position.y + 10; // Slight offset below cursor
    showArrow = false;
    
    // Ensure the preview doesn't go off-screen
    leftPosition = Math.max(20, Math.min(leftPosition, window.innerWidth - previewWidth - 20));
    topPosition = Math.max(20, Math.min(topPosition, window.innerHeight - previewHeight - 20));
  }

  return ReactDOM.createPortal(
    <div className="event-preview-overlay" onClick={handleOverlayClick} style={{ display: isOpen ? 'block' : 'none' }}>
      <div 
        className={`event-preview ${showDescriptionOptions ? 'expanded' : ''}`}
        style={{
          position: 'absolute',
          left: `${leftPosition}px`,
          top: `${topPosition}px`,
          transform: 'none'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Arrow pointing to the clicked event */}
        {showArrow && (
          <div 
            className="preview-arrow"
            style={{
              position: 'absolute',
              left: '-14px',
              top: '50%',
              transform: 'translateY(-50%)'
            }}
          >
            <svg width="14" height="28" viewBox="0 0 14 28" fill="none">
              <path d="M0 14L14 0V28L0 14Z" fill="#E7E7E5" stroke="#C7C7C7" strokeWidth="1"/>
            </svg>
          </div>
        )}

        {/* Header with close and expand buttons */}
        <div className="preview-header">
          <button className="expand-btn" onClick={() => onExpand(event || {
            title: editedTitle,
            location: editedLocation,
            description: editedDescription,
            start: event?.start || (selectedDate && (() => {
              const startDate = new Date(selectedDate);
              if (editedStartTime) {
                const [hour, minute] = editedStartTime.split(':').map(Number);
                startDate.setHours(hour, minute, 0, 0);
              } else {
                startDate.setHours(9, 0, 0, 0);
              }
              return startDate;
            })()),
            end: event?.end || (selectedDate && (() => {
              const endDate = new Date(selectedDate);
              if (editedEndTime) {
                const [hour, minute] = editedEndTime.split(':').map(Number);
                endDate.setHours(hour, minute, 0, 0);
              } else {
                endDate.setHours(10, 0, 0, 0);
              }
              return endDate;
            })()),
            resource: event?.resource,
            employee: event?.employee,
            isNew: isCreating,
            participants: selectedParticipants.map(p => p.email),
            uploadedFiles: uploadedFiles,
            zoomInfo: zoomInfo || event?.zoom_info
          })} title="Expand">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 3 21 3 21 9"/>
              <polyline points="9 21 3 21 3 15"/>
              <line x1="21" y1="3" x2="14" y2="10"/>
              <line x1="3" y1="21" x2="10" y2="14"/>
            </svg>
          </button>
          
          <button className="close-btn" onClick={onClose} title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Title Section */}
        <div className="preview-section title-section">
          {editingTitle ? (
            <div className="edit-field-container">
              <input
                type="text"
                className="edit-input title-edit"
                value={editedTitle}
                onChange={(e) => setEditedTitle(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, 'title')}
                placeholder="Add title"
                autoFocus
              />
            </div>
          ) : (
            <div 
              className="preview-field title-field"
              onClick={() => setEditingTitle(true)}
            >
              <h3 className="event-title">{(event?.title || editedTitle) || (isCreating ? 'New Event' : 'Untitled Event')}</h3>
            </div>
          )}
        </div>

        {/* Time Section */}
        <div className="preview-section time-section">
          <div 
            className="preview-field time-field"
            onClick={() => setEditingTime(true)}
          >
            <span className="field-text">{formatTimeRange()}</span>
          </div>
        </div>

        {/* Location Section */}
        <div className="preview-section location-section">
          <div className="preview-field location-field">
            <div className="location-content">
              <div className="location-input-container">
                <input
                  type="text"
                  value={editedLocation}
                  onChange={(e) => setEditedLocation(e.target.value)}
                  placeholder="Add Location or Video Call"
                  className="field-input"
                />
                <div className="video-call-icon">
                  <button
                    onClick={createZoomMeeting}
                    disabled={isCreatingZoom}
                    className="video-call-button"
                    title="Create Zoom meeting"
                  >
                    {isCreatingZoom ? (
                      <div className="loading-spinner"></div>
                    ) : (
                      <svg width="16" height="10" viewBox="0 0 20 13" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.75 0C2.02065 0 1.32118 0.289731 0.805456 0.805456C0.289731 1.32118 0 2.02065 0 2.75V10.25C0 10.9793 0.289731 11.6788 0.805456 12.1945C1.32118 12.7103 2.02065 13 2.75 13H11.25C11.9793 13 12.6788 12.7103 13.1945 12.1945C13.7103 11.6788 14 10.9793 14 10.25V8.656L17.419 11.701C18.224 12.418 19.5 11.846 19.5 10.767V1.865C19.5 0.785 18.224 0.214 17.419 0.931L14 3.976V2.75C14 2.02065 13.7103 1.32118 13.1945 0.805456C12.6788 0.289731 11.9793 0 11.25 0H2.75Z"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              
              {/* Display Zoom meeting details if available */}
              {(zoomInfo || event?.zoom_info) && (
                <div className="zoom-meeting-details">
                  <div className="zoom-meeting-header">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17 10.5V7C17 6.73478 16.8946 6.48043 16.7071 6.29289C16.5196 6.10536 16.2652 6 16 6H4C3.73478 6 3.48043 6.10536 3.29289 6.29289C3.10536 6.48043 3 6.73478 3 7V17C3 17.2652 3.10536 17.5196 3.29289 17.7071C3.48043 17.8946 3.73478 18 4 18H16C16.2652 18 16.5196 17.8946 16.7071 17.7071C16.8946 17.5196 17 17.2652 17 17V13.5L21 17.5V6.5L17 10.5Z" fill="#0B5CFF"/>
                    </svg>
                    <span className="zoom-meeting-title">Zoom Meeting</span>
                  </div>
                  <div className="zoom-meeting-info">
                    <div className="zoom-info-item">
                      <span className="zoom-info-label">Meeting ID:</span>
                      <span className="zoom-info-value">{(zoomInfo || event?.zoom_info)?.id}</span>
                    </div>
                    {(zoomInfo || event?.zoom_info)?.passcode && (
                      <div className="zoom-info-item">
                        <span className="zoom-info-label">Passcode:</span>
                        <span className="zoom-info-value">{(zoomInfo || event?.zoom_info)?.passcode}</span>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => window.open((zoomInfo || event?.zoom_info)?.joinUrl, '_blank')}
                    className="zoom-join-button"
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M23 7L16 12L23 17V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <rect x="1" y="5" width="15" height="14" rx="2" ry="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    Join Meeting
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Participants Section */}
        <div className="preview-section participants-section">
          <div className="preview-field participants-field">
            {editingParticipants ? (
              <div className="participants-inline-edit">
                <div className="participants-search-wrapper">
                  <input
                    ref={participantInputRef}
                    type="text"
                    className="participant-search-simple"
                    value={participantSearch}
                    onChange={(e) => setParticipantSearch(e.target.value)}
                    placeholder="Add participants..."
                    autoFocus
                  />
                  
                  {showParticipantsDropdown && filteredEmployees.length > 0 && (
                    <div className="participants-dropdown-simple">
                      {filteredEmployees.map((employee) => (
                        <div
                          key={employee.email}
                          className="participant-item-simple"
                          onClick={() => {
                            handleParticipantSelect(employee);
                            setEditingParticipants(false);
                            setParticipantSearch('');
                          }}
                        >
                          {employee.first_name && employee.last_name 
                            ? `${employee.first_name} ${employee.last_name}`
                            : employee.email
                          }
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <span 
                className="field-text"
                onClick={() => setEditingParticipants(true)}
              >
                {selectedParticipants.length > 0 
                  ? `${selectedParticipants.length} participant${selectedParticipants.length > 1 ? 's' : ''}`
                  : 'Add Invitees'
                }
              </span>
            )}
          </div>
          
          {/* Show selected participants below */}
          {selectedParticipants.length > 0 && (
            <div className="selected-participants-list">
              {selectedParticipants.map((participant) => (
                <div key={participant.email} className="selected-participant-item">
                  <span className="participant-name">
                    {participant.first_name && participant.last_name 
                      ? `${participant.first_name} ${participant.last_name}`
                      : participant.email
                    }
                  </span>
                  <button
                    type="button"
                    className="remove-participant-btn"
                    onClick={() => handleParticipantRemove(participant.email)}
                    title="Remove participant"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Description Section */}
        <div className="preview-section description-section">
          {!showDescriptionOptions && !editingDescription ? (
            <div 
              className="preview-field description-field"
              onClick={() => setShowDescriptionOptions(true)}
            >
              <span className="field-text">
                {(event?.description || editedDescription) || 'Add notes, URL or attachments'}
              </span>
            </div>
          ) : null}
          
          {showDescriptionOptions && !editingDescription ? (
            <>
              {/* Add Notes Option */}
              <div 
                className="preview-field"
                onClick={() => handleDescriptionOptionSelect('notes')}
              >
                <span className="field-text">Add Notes</span>
              </div>
              
              {/* Add URL Option */}
              <div 
                className="preview-field"
                onClick={() => handleDescriptionOptionSelect('url')}
              >
                <span className="field-text">Add URL</span>
              </div>
              
              {/* Add Attachment Option */}
              <div 
                className="preview-field"
                onClick={() => handleDescriptionOptionSelect('attachments')}
              >
                <span className="field-text">Add Attachment...</span>
              </div>
            </>
          ) : null}
          
          {/* Show editing field when in edit mode */}
          {editingDescription && (
            <div className="edit-field-container">
              <textarea
                className="edit-input description-edit"
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                onBlur={() => {
                  if (!isCreating) handleBlur('description');
                  setEditingDescription(false);
                  setShowDescriptionOptions(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    setEditingDescription(false);
                    setShowDescriptionOptions(false);
                  } else {
                    handleKeyDown(e, 'description');
                  }
                }}
                placeholder={getDescriptionPlaceholder()}
                rows={2}
                autoFocus
              />
            </div>
          )}
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
        />

        {/* Uploaded Files Section */}
        {uploadedFiles.length > 0 && (
          <div className="preview-section files-section">
            <div className="files-list">
              {uploadedFiles.map((file, index) => (
                <div key={index} className="file-item-preview">
                  <div className="file-info">
                    <span className="file-name">{file.originalName || file.name}</span>
                    <span className="file-size">{formatFileSize(file.size)}</span>
                  </div>
                  <button
                    type="button"
                    className="remove-file-btn"
                    onClick={() => removeFile(index)}
                    title="Remove file"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons for new events */}
        {isCreating && (
          <div className="preview-actions">
            <button
              type="button"
              className="save-btn"
              onClick={handleCreateEvent}
              disabled={!editedTitle.trim()}
            >
              Create Event
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default EventPreview; 